import React, { forwardRef, useImperativeHandle, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DeleteIcon from '../../static/image/deleteIcon.svg';
import { setValuePlans } from '../../redux/productSlice';
import CustomInput from '../../components/customInput/CustomInput';
import CommonDeleteModal from '../../components/commonDeleteModal/CommonDeleteModal';

const PricePlan = forwardRef(({ language }, ref) => {
  const dispatch = useDispatch();
  const [benefitsErrors, setBenefitsErrors] = useState({});
  const [benefitsTempValues, setBenefitsTempValues] = useState({});
  const [showDeleteGroupModal, setShowDeleteGroupModal] = useState(false);
  const [groupToDeleteIndex, setGroupToDeleteIndex] = useState(null);

  const productState = useSelector((state) => state.product);
  const product = productState.data || {};
  const error = productState.error || {};

  const plansData = product.plans || product.contentData?.plans || {};
  const pricePlans = plansData.pricePlans || [];
  const plansTitle = plansData.title || '';
  const plansDescription = plansData.description || '';
  const tableHead = plansData.tableHead || '';
  const bottomDesc = plansData.bottomDesc || '';

  const getDefaultBenefitsValue = () => {
    return `    {\n        "title": "",\n        "desc": "",\n        "icon": ""\n    }`;
  };

  useEffect(() => {
    const tempValues = {};
    pricePlans.forEach((plan, index) => {
      const normalizedBenefits = (plan.benefits || []).map(benefit => {
        if (typeof benefit === 'object' && benefit !== null) {
          return {
            title: benefit.title || "",
            desc: benefit.desc || "",
            icon: benefit.icon || ""
          };
        }
        return benefit;
      });

      if (!normalizedBenefits || normalizedBenefits.length === 0 ||
        (normalizedBenefits.length === 1 &&
          normalizedBenefits[0].title === "" &&
          normalizedBenefits[0].desc === "" &&
          normalizedBenefits[0].icon === "")) {
        tempValues[index] = getDefaultBenefitsValue();
      } else {
        tempValues[index] = getBenefitsDisplayValue(normalizedBenefits);
      }
    });
    setBenefitsTempValues(tempValues);
  }, [pricePlans, language]);

  const PLACEHOLDER_CONFIG = {
    title: {
      EN: "Enter title EN",
      CN: "Enter title CN"
    },
    description: {
      EN: "Enter description EN",
      CN: "Enter description CN"
    },
    tableHead: {
      EN: "Enter table head EN",
      CN: "Enter table head CN"
    },
    bottomDesc: {
      EN: "Enter bottom description EN",
      CN: "Enter bottom description CN"
    },
    topTitle: {
      EN: "Enter top title EN",
      CN: "Enter top title CN"
    },
    topTitleColor: {
      EN: "Enter top title color EN",
      CN: "Enter top title color CN"
    },
    planTitle: {
      EN: "Enter plan title EN",
      CN: "Enter plan title CN"
    },
    planSubtitle: {
      EN: "Enter plan subtitle EN",
      CN: "Enter plan subtitle CN"
    },
    pricing: {
      EN: "Enter pricing EN",
      CN: "Enter pricing CN"
    },
    priceNote: {
      EN: "Enter price note EN",
      CN: "Enter price note CN"
    },
    promoType: {
      EN: "Enter promo type EN",
      CN: "Enter promo type CN"
    },
    promoDesc: {
      EN: "Enter promo description EN",
      CN: "Enter promo description CN"
    },
    remarkPricing: {
      EN: "Enter pricing remark EN",
      CN: "Enter pricing remark CN"
    },
    planDescription: {
      EN: "Enter plan description EN",
      CN: "Enter plan description CN"
    },
    benefits: {
      EN: "Enter benefits (one per line) EN",
      CN: "Enter benefits (one per line) CN"
    }
  };

  const updatePlans = (field, value) => {
    dispatch(setValuePlans({
      value: {
        ...plansData,
        [field]: value
      }
    }));
  };

  const addGroup = () => {
    const newIndex = pricePlans.length;
    const newPricePlans = [
      ...pricePlans,
      {
        topTitle: "",
        topTitleColor: "",
        planTitle: "",
        planSubtitle: "",
        pricing: "",
        priceNote: "",
        promoType: "",
        promoDesc: "",
        remarkPricing: "",
        planDescription: "",
        benefits: [
          { title: "", desc: "", icon: "" }
        ]
      }
    ];
    dispatch(setValuePlans({
      value: {
        ...plansData,
        pricePlans: newPricePlans
      }
    }));
    setBenefitsErrors(prev => ({ ...prev, [newIndex]: '' }));
    setBenefitsTempValues(prev => ({
      ...prev,
      [newIndex]: getDefaultBenefitsValue()
    }));
  };

  const showDeleteGroupConfirmation = (index) => {
    if (pricePlans.length <= 1) {
      alert('At least retain one set of data');
      return;
    }
    setGroupToDeleteIndex(index);
    setShowDeleteGroupModal(true);
  };

  const confirmDeleteGroup = () => {
    const newPricePlans = pricePlans.filter((_, i) => i !== groupToDeleteIndex);
    dispatch(setValuePlans({
      value: {
        ...plansData,
        pricePlans: newPricePlans
      }
    }));
    setShowDeleteGroupModal(false);
    setGroupToDeleteIndex(null);

    // Clean up error states
    const newErrors = { ...benefitsErrors };
    delete newErrors[groupToDeleteIndex];
    setBenefitsErrors(newErrors);

    const newTempValues = { ...benefitsTempValues };
    delete newTempValues[groupToDeleteIndex];
    setBenefitsTempValues(newTempValues);
  };

  const cancelDeleteGroup = () => {
    setShowDeleteGroupModal(false);
    setGroupToDeleteIndex(null);
  };

  const updateGroup = (index, field, value) => {
    const newPricePlans = pricePlans.map((plan, i) =>
      i === index ? { ...plan, [field]: value } : plan
    );
    dispatch(setValuePlans({
      value: {
        ...plansData,
        pricePlans: newPricePlans
      }
    }));
  };

  const getBenefitsDisplayValue = (benefits) => {
    if (!benefits || benefits.length === 0) return '';

    try {
      if (Array.isArray(benefits)) {
        const benefitsString = benefits.map(benefit => {
          if (typeof benefit === 'object' && benefit !== null) {
            return `    {\n        "title": "${benefit.title || ''}",\n        "desc": "${benefit.desc || ''}",\n        "icon": "${benefit.icon || ''}"\n    }`;
          }
          return `    ${JSON.stringify(benefit)}`;
        }).join(',\n');

        return benefitsString;
      }

      return benefits;
    } catch (error) {
      return benefits;
    }
  };

  const validateJsonStructure = (value) => {
    try {
      if (!value.trim() || value.trim() === getDefaultBenefitsValue().trim()) {
        return { valid: true };
      }

      const arr = JSON.parse(`[${value}]`);
      if (!Array.isArray(arr)) {
        return { valid: false, message: 'Must be an array' };
      }

      const allowedFields = ['title', 'desc', 'icon'];

      for (const item of arr) {
        if (typeof item !== 'object' || item === null) {
          return { valid: false, message: 'Each item must be an object' };
        }

        const itemFields = Object.keys(item);
        const invalidFields = itemFields.filter(field => !allowedFields.includes(field));
        if (invalidFields.length > 0) {
          return {
            valid: false,
            message: `Invalid fields: ${invalidFields.join(', ')}. Only "title", "desc", "icon" are allowed.`
          };
        }

        if (
          typeof item.title !== 'string' ||
          typeof item.desc !== 'string' ||
          typeof item.icon !== 'string'
        ) {
          return {
            valid: false,
            message: 'Each object must contain string type "title", "desc" and "icon"'
          };
        }
      }

      return { valid: true };
    } catch (e) {
      return { valid: false, message: `Invalid JSON: ${e.message}` };
    }
  };

  const handleBenefitsInput = (index, value) => {
    setBenefitsTempValues(prev => ({ ...prev, [index]: value }));
  };

  const handleBenefitsBlur = (index) => {
    const value = benefitsTempValues[index] || '';

    if (value.trim() === getDefaultBenefitsValue().trim()) {
      setBenefitsErrors(prev => ({ ...prev, [index]: '' }));
      updateGroup(index, 'benefits', []);
      return;
    }

    const validation = validateJsonStructure(value);

    if (!validation.valid) {
      setBenefitsErrors(prev => ({ ...prev, [index]: validation.message }));
      return;
    }

    setBenefitsErrors(prev => ({ ...prev, [index]: '' }));

    try {
      let benefitsValue;
      if (value.trim() === '') {
        benefitsValue = [];
      } else {
        const wrappedValue = `[${value}]`;
        benefitsValue = JSON.parse(wrappedValue);
        if (!Array.isArray(benefitsValue)) {
          benefitsValue = [benefitsValue];
        }

        benefitsValue = benefitsValue.map(item => ({
          title: item.title || "",
          desc: item.desc || "",
          icon: item.icon || ""
        }));
      }
      updateGroup(index, 'benefits', benefitsValue);
    } catch (error) {
      setBenefitsErrors(prev => ({ ...prev, [index]: `Error: ${error.message}` }));
    }
  };

  const validateAllBenefits = () => {
    const errors = {};
    Object.keys(benefitsTempValues).forEach(index => {
      const value = benefitsTempValues[index] || '';
      const validation = validateJsonStructure(value);
      if (!validation.valid) {
        errors[index] = validation.message;
        setBenefitsErrors(prev => ({ ...prev, [index]: validation.message }));
      }
    });
    return errors;
  };

  const getFieldError = (index, field) => {
    const errorKey = `pricePlan_${index}_${field}`;
    return error[errorKey];
  };

  const isFieldRequired = (fieldId) => {
    return false;
  };

  useImperativeHandle(ref, () => ({
    addGroup,
    validateAllBenefits
  }));

  return (
    <div className="tab-package-container">
      {/* Header Section */}
      <div className="domain-group">
        <div className='package-input'>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <label>Title:</label>
          </div>
          <CustomInput
            type="text"
            value={plansTitle}
            onChange={(e) => updatePlans('title', e.target.value)}  // 改为updatePlans
            placeholder={PLACEHOLDER_CONFIG.title[language]}
            customStyle={{ width: '73%' }}
          />
        </div>

        <div className='package-input'>
          <label>Description:</label>
          <textarea
            placeholder={PLACEHOLDER_CONFIG.description[language]}
            value={plansDescription || ''}
            onChange={(e) => updatePlans('description', e.target.value)}
            rows={3}
            style={{ width: '73%' }}
          />
        </div>

        <div className='package-input'>
          <label>Table Head:</label>
          <input
            placeholder={PLACEHOLDER_CONFIG.tableHead[language]}
            value={tableHead || ''}
            onChange={(e) => updatePlans('tableHead', e.target.value)}
            style={{ width: '73%' }}
          />
        </div>

        <div className='package-input'>
          <label>Bottom Description:</label>
          <textarea
            placeholder={PLACEHOLDER_CONFIG.bottomDesc[language]}
            value={bottomDesc || ''}
            onChange={(e) => updatePlans('bottomDesc', e.target.value)}
            rows={3}
            style={{ width: '73%' }}
          />
        </div>
      </div>

      <div
        style={{
          height: '0.5px',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          margin: '25px -40px',
          width: 'calc(100% + 80px)',
          display: 'block',
          boxSizing: 'border-box'
        }}
      />

      {/* Price Plans List */}
      {pricePlans.map((plan, index) => (
        <div key={index}>
          <div className="domain-group">

            <div className='package-input'>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <label>
                  Plan Title:
                </label>
              </div>
              <CustomInput
                type="text"
                value={plan.planTitle || ''}
                onChange={(e) => updateGroup(index, 'planTitle', e.target.value)}
                placeholder={PLACEHOLDER_CONFIG.planTitle[language]}
                customStyle={{ width: '73%' }}
                error={getFieldError(index, 'planTitle')}
              />
              <button className="icon-btn" onClick={() => showDeleteGroupConfirmation(index)}>
                <img src={DeleteIcon} alt="Delete" className="campaign-delete-icon" />
              </button>
            </div>
            <div className='package-input'>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <label>
                  Top Title:
                </label>
              </div>
              <CustomInput
                type="text"
                value={plan.topTitle || ''}
                onChange={(e) => updateGroup(index, 'topTitle', e.target.value)}
                placeholder={PLACEHOLDER_CONFIG.topTitle[language]}
                customStyle={{ width: '73%' }}
              />
            </div>

            <div className='package-input'>
              <label>Top Title Color:</label>
              <input
                placeholder={PLACEHOLDER_CONFIG.topTitleColor[language]}
                value={plan.topTitleColor || ''}
                onChange={(e) => updateGroup(index, 'topTitleColor', e.target.value)}
                style={{ width: '73%' }}
              />
            </div>


            <div className='package-input'>
              <label>Plan Subtitle:</label>
              <input
                placeholder={PLACEHOLDER_CONFIG.planSubtitle[language]}
                value={plan.planSubtitle || ''}
                onChange={(e) => updateGroup(index, 'planSubtitle', e.target.value)}
              />
            </div>

            <div className='package-input'>
              <label>Pricing:</label>
              <input
                type="number"
                placeholder={PLACEHOLDER_CONFIG.pricing[language]}
                value={plan.pricing || ''}
                onChange={(e) => updateGroup(index, 'pricing', e.target.value)}
                style={{ width: '73%' }}
              />
            </div>

            <div className='package-input'>
              <label>Price Note:</label>
              <input
                placeholder={PLACEHOLDER_CONFIG.priceNote[language]}
                value={plan.priceNote || ''}
                onChange={(e) => updateGroup(index, 'priceNote', e.target.value)}
                style={{ width: '73%' }}
              />
            </div>

            <div className='package-input'>
              <label>Promo Type:</label>
              <input
                placeholder={PLACEHOLDER_CONFIG.promoType[language]}
                value={plan.promoType || ''}
                onChange={(e) => updateGroup(index, 'promoType', e.target.value)}
                style={{ width: '73%' }}
              />
            </div>

            <div className='package-input'>
              <label>Promo Desc:</label>
              <input
                placeholder={PLACEHOLDER_CONFIG.promoDesc[language]}
                value={plan.promoDesc || ''}
                onChange={(e) => updateGroup(index, 'promoDesc', e.target.value)}
                style={{ width: '73%' }}
              />
            </div>

            <div className='package-input'>
              <label>Remark Pricing:</label>
              <input
                placeholder={PLACEHOLDER_CONFIG.remarkPricing[language]}
                value={plan.remarkPricing || ''}
                onChange={(e) => updateGroup(index, 'remarkPricing', e.target.value)}
                style={{ width: '73%' }}
              />
            </div>

            <div className='package-input'>
              <label>Plan Description:</label>
              <textarea
                placeholder={PLACEHOLDER_CONFIG.planDescription[language]}
                value={plan.planDescription || ''}
                onChange={(e) => updateGroup(index, 'planDescription', e.target.value)}
                rows={3}
                style={{ width: '73%' }}
              />
            </div>

            <div className='package-input' style={{ marginBottom: '0px' }}>
              <label>Benefits:</label>
              <textarea
                placeholder={PLACEHOLDER_CONFIG.benefits[language]}
                value={benefitsTempValues[index] || getDefaultBenefitsValue()}
                onChange={(e) => handleBenefitsInput(index, e.target.value)}
                onBlur={() => handleBenefitsBlur(index)}
                rows={6}
                style={{
                  borderColor: benefitsErrors[index] ? 'red' : '',
                  marginBottom: '4px',
                  width: '73%'
                }}
              />
            </div>
            {benefitsErrors[index] && (
              <div style={{ color: 'red', marginTop: '4px' }}>
                {benefitsErrors[index]}
              </div>
            )}
          </div>

          {index !== pricePlans.length - 1 && (
            <div
              style={{
                height: '0.5px',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                margin: '25px -40px',
                width: 'calc(100% + 80px)',
                display: 'block',
                boxSizing: 'border-box'
              }}
            />
          )}
        </div>
      ))}

      <CommonDeleteModal
        show={showDeleteGroupModal}
        onHide={cancelDeleteGroup}
        onConfirm={confirmDeleteGroup}
        title="Delete Price Plan"
        instruction="Are you sure you want to delete this price plan?"
      />
    </div>
  );
});

export default PricePlan;